import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import { H2 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import DiscoveryTags from '@/components/DiscoveryTags/DiscoveryTags';
import { upcomingItemPageItemCard } from '@/components/GridItemCard';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import { CategoryLink } from '@/types/CategoryLink';
import { ItemFacets } from '@/types/ItemFacets';
import { Suggestions } from '@/types/SimilarItemInfo';
import { getBroadTitleSearchParams, getSimilarItemsUrl } from '@/utils/urls';

import FluidItemGrid from '../GridItemCard/FluidItemGrid/FluidItemGrid';
import { SeeAllLink } from '../SeeAllLink/SeeAllLink';

type Props = {
    catalogStatus: string;
    categoryLinks: CategoryLink[];
    centerHeader: boolean;
    className: string;
    facets: ItemFacets;
    getMoreSimilarItems: Function;
    getTagItems: Function;
    isLoading: boolean;
    itemTitle: string;
    moreResultsAvailable: boolean;
    popularItemIds: number[];
    selectedItems: any;
    suggestions: Suggestions[];
    title?: React.ReactNode;
    width: number;
};

const SimilarItems = (props: Props) => {
    const {
        categoryLinks,
        centerHeader = false,
        className = '',
        facets,
        getMoreSimilarItems,
        getTagItems,
        isLoading,
        itemTitle,
        moreResultsAvailable,
        popularItemIds,
        selectedItems,
        suggestions,
        title,
        width,
    } = props;

    const similarItemsUrl = getSimilarItemsUrl(itemTitle);
    const saveSearchParams = getBroadTitleSearchParams(itemTitle);

    const showPopular = selectedItems.length === 0;

    let linkMessage;
    let headerMessage;

    if (showPopular) {
        linkMessage = <FormattedMessage id="see_all" />;
        headerMessage = <FormattedMessage id="bid_most_popular_items" />;
    } else {
        headerMessage = title || <FormattedMessage id="discover_similar_items" />;
        linkMessage = (
            <>
                <FormattedMessage id="see_all" />
            </>
        );
    }

    return (
        <SimilarItemsContainer className={className}>
            <SimilarItemsHeader
                $centerText={centerHeader}
                key="header"
            >
                <H2>{headerMessage}</H2>
                <SaveSimilarSearchContainer>
                    <StyledSaveSearchContainer
                        allSearchParams={saveSearchParams}
                        saveSearchParams={saveSearchParams}
                        small
                    />
                    <SeeAllLink
                        text={linkMessage}
                        url={similarItemsUrl}
                    />
                </SaveSimilarSearchContainer>
            </SimilarItemsHeader>
            <DiscoveryTags
                categoryLinks={categoryLinks}
                facets={facets}
                getTagItems={getTagItems}
                key="discoveryTags"
                suggestions={suggestions}
                width={width}
            />
            {isLoading ? (
                <Throbber />
            ) : (
                <FluidItemGrid
                    itemCardOption={upcomingItemPageItemCard}
                    itemIds={showPopular ? popularItemIds : selectedItems}
                    key="grid"
                />
            )}
            {moreResultsAvailable && (
                <LoadMoreButton onClick={getMoreSimilarItems}>
                    <FormattedMessage id="loadMore" />
                </LoadMoreButton>
            )}
        </SimilarItemsContainer>
    );
};

export default React.memo<Props>(SimilarItems, isEqual);

const SaveSimilarSearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-left: auto;
    }
`;

const StyledSaveSearchContainer = styled(SaveSearchContainer)`
    margin-right: 10px;
`;

const LoadMoreButton = styled(Button)`
    margin: auto;
    margin-top: 32px;
    display: flex;
`;

const SimilarItemsContainer = styled.div`
    display: block;

    @media print {
        display: none;
    }
`;

const SimilarItemsHeader = styled.div<{ $centerText: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0;

    ${H2} {
        margin: 0 15px 5px 0;
        display: inline-block;

        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            margin-right: 0;
        }

        ${({ $centerText }) =>
            $centerText &&
            css`
                text-align: center;
            `}
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: column;
        align-items: flex-start;
        margin: 0 0 10px;
    }
`;
